import { defineStore } from 'pinia';
import { type Component, computed, markRaw, ref, shallowRef } from 'vue';
import type { ApplicationStatusProps } from '@/components/layout/ApplicationStatus/applicationStatus.ts';
import {
  DEFAULT_COMPACT_VALUE,
  DEFAULT_NOTIFICATION_STATUS,
  type NotificationBoxStatus,
} from '@/components/common/NotificationBox/notificationBox.ts';
import type { TranslatableLabel } from '@/types';
import { isTranslatableLabel } from '@/utils/i18n.ts';

/**
 * Gestion de l'affichage des boîtes de status et/ou notifications au travers
 * des différentes pages de l'applications
 */
const applicationStatusStore = defineStore('applicationStatus', () => {
  const opened = ref<boolean>(false);
  const id = ref<string>();
  const title = ref<string | TranslatableLabel>('');
  const compact = ref<boolean>(DEFAULT_COMPACT_VALUE);
  const status = ref<NotificationBoxStatus>(DEFAULT_NOTIFICATION_STATUS);
  const content = shallowRef<TranslatableLabel | Component | string>('');
  const component = shallowRef<Component>();

  function closeApplicationStatus(): void {
    opened.value = false;
    compact.value = DEFAULT_COMPACT_VALUE;
    title.value = '';
    status.value = DEFAULT_NOTIFICATION_STATUS;
    id.value = undefined;
    resetBoxContent();
  }

  /**
   * Suppression du contenu de la boîte de statut. Comme ce contenu est représenté par des shallowRef,
   * il est appelé dès qu'il change, même si la boîte de statut reste ouverte.
   */
  function resetBoxContent(): void {
    component.value = undefined;
    content.value = '';
  }

  function openApplicationStatus(payload: ApplicationStatusProps): void {
    resetBoxContent(); // suppression du contenu de la boîte, advenant le cas où le statut est déjà ouvert avec du contenu dedans.
    id.value = payload.id;

    if (payload.custom) {
      component.value = payload.component
        ? markRaw(payload.component)
        : undefined;
    } else {
      compact.value = payload.compact ?? DEFAULT_COMPACT_VALUE;
      content.value = typeof payload.content === 'string' || !payload.content || isTranslatableLabel(payload.content)
        ? payload.content || ''
        : markRaw(payload.content);
      status.value = payload.status ?? DEFAULT_NOTIFICATION_STATUS;
      title.value = payload.title ?? '';
    }

    opened.value = true;
  }

  const statusProps = computed<ApplicationStatusProps | undefined>(() => opened.value
    ? {
      compact: compact.value,
      status: status.value,
      title: title.value,
      content: content.value,
      custom: !!component.value,
      component: component.value,
      id: id.value,
    }
    : undefined);

  const isOpened = computed<boolean>(() => opened.value);

  return {
    closeApplicationStatus,
    openApplicationStatus,
    statusProps,
    isOpened,
  };
});

export default applicationStatusStore;
